/**
 * Chronik (Geschichte & Werte) — Custom Element <chronik-akt>
 * Baut die sticky Decade-Rail aus dem gerenderten DOM und steuert
 * Chapter-Glide-In, Active-Highlighting und Rail-Engagement.
 *
 * connectedCallback baut auf und registriert Listener (AbortController),
 * disconnectedCallback räumt restlos auf — verhindert Listener-/rAF-
 * Akkumulation bei Section-Re-Render im Theme-Editor.
 * Wird von sections/chronik.liquid eingebunden.
 */
if (!customElements.get('chronik-akt')) {
  class ChronikAkt extends HTMLElement {
    #controller = null;
    #rafId = 0;
    #built = false;

    connectedCallback() {
      const akt = this.querySelector('.akt--hg-chronik');
      if (!akt) return;

      const chapters = [...akt.querySelectorAll('.hg-chapter')];
      if (!chapters.length) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const railAside = akt.querySelector('.hg-chronik-rail');
      const railInner = akt.querySelector('.hg-chronik-rail-inner');

      // Rail einmalig aus dem DOM aufbauen (Theme-Block-Settings sind im
      // Liquid nicht zugänglich; Chapter liefern data-year + id).
      if (railAside && railInner && !this.#built) {
        const frag = document.createDocumentFragment();
        for (const c of chapters) {
          const year = c.dataset.year;
          const id = c.id;
          if (!year || !id) continue;
          const li = document.createElement('li');
          const a = document.createElement('a');
          a.href = '#' + id;
          a.dataset.year = year;
          const tick = document.createElement('span');
          tick.className = 'rail-tick';
          tick.setAttribute('aria-hidden', 'true');
          const yearEl = document.createElement('span');
          yearEl.className = 'rail-year';
          yearEl.textContent = year;
          a.append(tick, yearEl);
          li.appendChild(a);
          frag.appendChild(li);
        }
        if (frag.childNodes.length) {
          railInner.appendChild(frag);
          railAside.hidden = false;
          this.#built = true;
        }
      }

      this.#controller = new AbortController();
      const { signal } = this.#controller;

      // Rail-Link-Klicks: weicher Scroll zum Chapter.
      for (const a of akt.querySelectorAll('.hg-chronik-rail-inner a')) {
        a.addEventListener(
          'click',
          (e) => {
            const id = a.getAttribute('href').slice(1);
            const target = document.getElementById(id);
            if (!target) return;
            e.preventDefault();
            target.classList.add('is-in');
            target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
            if (history.replaceState) history.replaceState(null, '', '#' + id);
          },
          { signal }
        );
      }

      if (reducedMotion) {
        for (const c of chapters) c.classList.add('is-in');
        if (railInner) railInner.classList.add('is-engaged');
        return;
      }

      const railLinks = [...akt.querySelectorAll('.hg-chronik-rail-inner a')];

      const triggerChapters = () => {
        const triggerLine = window.innerHeight * 0.6;
        for (const c of chapters) {
          if (c.classList.contains('is-in')) continue;
          const r = c.getBoundingClientRect();
          if (r.top < triggerLine && r.bottom > 0) {
            c.classList.add('is-in');
          }
        }
      };

      const updateRailActive = () => {
        if (!railLinks.length) return;
        const vpMid = window.innerHeight / 2;
        let bestChapter = null;
        let bestDistance = Infinity;
        for (const c of chapters) {
          const r = c.getBoundingClientRect();
          if (r.bottom < 0 || r.top > window.innerHeight) continue;
          const mid = (r.top + r.bottom) / 2;
          const d = Math.abs(mid - vpMid);
          if (d < bestDistance) {
            bestDistance = d;
            bestChapter = c;
          }
        }
        const activeYear = bestChapter ? bestChapter.dataset.year : null;
        for (const a of railLinks) {
          a.classList.toggle('is-active', a.dataset.year === activeYear);
        }
      };

      const updateRailVisibility = () => {
        if (!railInner) return;
        const r = akt.getBoundingClientRect();
        const halfVp = window.innerHeight / 2;
        const inChronik = r.top < halfVp && r.bottom > halfVp;
        railInner.classList.toggle('is-engaged', inChronik);
      };

      // Scroll/Resize über requestAnimationFrame drosseln.
      const onScroll = () => {
        if (this.#rafId) return;
        this.#rafId = requestAnimationFrame(() => {
          this.#rafId = 0;
          triggerChapters();
          updateRailActive();
          updateRailVisibility();
        });
      };

      onScroll();
      window.addEventListener('load', onScroll, { signal });
      window.addEventListener('scroll', onScroll, { passive: true, signal });
      window.addEventListener('resize', onScroll, { passive: true, signal });
    }

    disconnectedCallback() {
      this.#controller?.abort();
      if (this.#rafId) cancelAnimationFrame(this.#rafId);
      this.#rafId = 0;
    }
  }

  customElements.define('chronik-akt', ChronikAkt);
}
